import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { countyLabel, countyNoun } from "@/lib/county-label";
import {
  CDC_STATUS_SOURCE,
  CDC_STATUS_VAR,
  countyHeadline,
  type CdcCountyStatus,
} from "@/lib/county-status-text";

/**
 * When an answer knows the reader's county (from "Use my location" or a county search),
 * CDC's record for that county comes first, above the state's card. The record is looked
 * up on the device after the answer appears: CDC's lists are a small chunk loaded only
 * now, so the home page's first load stays light.
 */
export function CountyRecordLine({
  county,
  stateAbbr,
  stateSlug,
  pageEnabled,
  note,
}: {
  county: { fips: string; name: string; slug: string };
  stateAbbr: string;
  stateSlug: string;
  /** Whether the county has its own page to link to. */
  pageEnabled: boolean;
  /** How the county was found, e.g. "ZIP 78701 is mostly in Travis County." */
  note?: string;
}) {
  const [status, setStatus] = useState<CdcCountyStatus | null>(null);

  useEffect(() => {
    let live = true;
    setStatus(null);
    import("@/lib/county-status").then((m) => {
      if (live) setStatus(m.cdcCountyStatus(county.fips));
    });
    return () => {
      live = false;
    };
  }, [county.fips]);

  const label = countyLabel({ ...county, stateAbbr });
  return (
    <section
      aria-label={`CDC's record for ${label}`}
      className="border-l-[5px] py-1 pl-4 sm:pl-5"
      style={{ borderLeftColor: status ? CDC_STATUS_VAR[status] : "var(--border)" }}
    >
      <p className="label-caps text-muted-foreground">
        {label}, CDC record {CDC_STATUS_SOURCE.year}
      </p>
      <p className="mt-1.5 min-h-[1.75rem] font-display text-[1.375rem] leading-snug text-foreground">
        {status ? countyHeadline(label, status) : `Checking CDC's record for ${label}…`}
      </p>
      {note && <p className="mt-1 text-sm text-muted-foreground">{note}</p>}
      {pageEnabled && (
        <Link
          to="/states/$state/$county"
          params={{ state: stateSlug, county: county.slug }}
          className="link mt-1 inline-block text-sm font-semibold"
        >
          The {label} page: a map of the {countyNoun(stateAbbr, true)} around it
        </Link>
      )}
    </section>
  );
}
